import { useSetAtom } from 'jotai';
import * as _ from 'lodash-es';
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import * as styles from './ControlBarAddOrSharePopOver.module.scss';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import BorderedIcon from '../../components/BorderedIcon';
import Button from '../../components/Button';
import ExpandableDropdown from '../../components/ExpandableDropdown';
import SelectEntityOption from '../../components/SelectEntityOption';
import Toast from '../../components/Toast';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import { TopicPrivacy } from '../../graphql/generated/graphql';
import GroupSelectOption from '../../groups/GroupSelectOption';
import addIcon from '../../icons/non-prefilled/16x16/add.svg';
import imageIcon from '../../icons/non-prefilled/16x16/image.svg';
import seriesIcon from '../../icons/non-prefilled/16x16/series.svg';
import verificationIcon from '../../icons/non-prefilled/16x16/verified.svg';
import type {
  UseEntitySearchConfig,
  UseEntitySearchGroupResult,
  UseEntitySearchTopicResult,
  UseEntitySearchUserResult,
} from '../../search/entitySearch/useEntitySearch';
import useEntitySearch from '../../search/entitySearch/useEntitySearch';
import AddSeriesModal from '../../series/modal/AddSeriesModal';
import { usePostSeries } from '../../series/useAllSeries';
import NewTopicModal from '../../topic/modal/NewTopicModal';
import topicPolicyCheck, {
  TopicPolicyAction,
} from '../../topic/topicPolicyCheck';
import TopicSelectOption from '../../topic/TopicSelectOption';
import { useAllTopicMap } from '../../topic/useAllTopics';
import { UIActions } from '../../ui/uiSlice';
import UserSelectOption from '../../user/UserSelectOption';
import exhaustiveMatchGuard from '../../utils/exhaustiveMatchGuard';
import populateObjects from '../../utils/populateObjects';
import { usePublicContentPermissions } from '../../utils/useAllowPublicContentBy';
import CreateVerificationModal from '../CreateVerificationModal';
import { isShareDropdownOpenAtom } from '../postView/PostEditButton';
import {
  useAddGroupToPost,
  useAddPostUser,
  useAddTopicToPost,
  useRemoveTopicFromPost,
} from '../sharing/postPermissionOperations';
import PostTopicToast, { shouldShowToast } from '../topic/PostTopicToast';
import useAddCover from '../useAddCover';
import useIsLayoutSize from '../utils/useIsLayoutSize';
import { LayoutSize } from '../utils/useLayoutSize';
import { useShouldRenderVerifyMenuItems } from '../VerifyMenuItems';

const POST_FRAGMENT = gql(`
  fragment ControlBarAddOrSharePopOver_post on Post {
    id
    publishedAt
    banner {
      original
    }
    verification {
      id
    }
    topics {
      id
    }
    ...useRemoveTopicFromPost_post
    ...useAddPostUser_post
    ...useShouldRenderVerifyMenuItems_post
    ...CreateVerificationModal_post
  }
`);

const useCreateTopic = (post: DocumentType<typeof POST_FRAGMENT>) => {
  const dispatch = useAppDispatch();
  const toastsRef = useRef<string[]>([]);

  const allTopicMap = useAllTopicMap();
  const postTopicIds = post.topics.map(({ id }) => id);
  const postTopics = populateObjects(allTopicMap, postTopicIds);

  const addTopic = useAddTopicToPost();
  const removeTopic = useRemoveTopicFromPost();

  const organizationName = useAppSelector(
    (state) => state.session.organization.name,
  );

  const dismissToasts = useCallback(
    () =>
      Promise.all(
        toastsRef.current.map((id) => dispatch(UIActions.removeToast(id))),
      ),
    [dispatch],
  );

  const getWarningCallback = useCallback(
    async (topic: EventHandlerTopic, adding: boolean) => {
      const isPublished = post.publishedAt !== null;

      if (
        postTopics &&
        shouldShowToast({
          oldTopics: postTopics,
          adding,
          newTopic: topic,
          organizationName,
          published: isPublished,
        })
      ) {
        await dismissToasts();
        // currently our redux typing doesn't consider thunks
        // eslint-disable-next-line  @typescript-eslint/await-thenable
        const id = (await dispatch(
          UIActions.pushToast(({ onDismiss }) => (
            <PostTopicToast
              topic={topic}
              adding={adding}
              addTopic={({ id: topicId }: { id: string }) => {
                addTopic(post.id, topicId);
              }}
              removeTopic={(t: Parameters<typeof removeTopic>[1]) => {
                removeTopic(post, t);
              }}
              organizationName={organizationName}
              published={isPublished}
              postTopics={postTopics}
              onDismiss={onDismiss}
            />
          )),
        )) as unknown;
        toastsRef.current.push(id as string);
      }
    },
    [
      addTopic,
      removeTopic,
      post,
      postTopics,
      dismissToasts,
      organizationName,
      dispatch,
    ],
  );

  const handleAdd = useCallback(
    async (topic: EventHandlerTopic) => {
      await addTopic(post.id, topic.id);
      getWarningCallback(topic, true);
    },
    [addTopic, post, getWarningCallback],
  );

  return useCallback(
    (newTopicName: string) => {
      dispatch(
        UIActions.openModal(NewTopicModal, {
          name: newTopicName,
          onCreate: handleAdd,
        }),
      );
    },
    [handleAdd, dispatch],
  );
};

const options: Parameters<typeof useEntitySearch>[2] = {
  limitPerType: 100,
};

type EventHandlerTopic = Parameters<typeof shouldShowToast>[number]['newTopic'];

interface ControlBarAddOrSharePopOverProps {
  onClose(): void;
  post: DocumentType<typeof POST_FRAGMENT>;
}

const ControlBarAddOrSharePopOver = ({
  onClose,
  post,
}: ControlBarAddOrSharePopOverProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);

  const [, startTransition] = useTransition();
  const [query, setQuery] = useState('');
  const [input, setInput] = useState('');
  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  const { isPermittedToPublicizePost } = usePublicContentPermissions();

  const config = useMemo((): UseEntitySearchConfig => {
    const { topics, sharing } = post;

    const topicIdSet = new Set(topics.map((topic) => topic.id));
    const userIdSet = new Set([
      ...(sharing.editorIds || []),
      ...(sharing.viewerIds || []),
    ]);

    const groupIdSet = new Set([
      ...(sharing.editorGroupIds || []),
      ...(sharing.viewerGroupIds || []),
    ]);

    return {
      topic: {
        filter: (topic) => {
          const isDisabled =
            topicIdSet.has(topic.id) ||
            !topicPolicyCheck(TopicPolicyAction.EditPost, topic, currentUser) ||
            (!isPermittedToPublicizePost &&
              topic.privacy === TopicPrivacy.Public);

          return !isDisabled;
        },
      },
      group: {
        filter: ({ id }) => !groupIdSet.has(id),
      },
      user: {
        filter: ({ id }) => !userIdSet.has(id),
      },
    };
  }, [post, currentUser, isPermittedToPublicizePost]);

  const handleCreateTopic = useCreateTopic(post);
  const addTopic = useAddTopicToPost();
  const addUser = useAddPostUser();
  const addGroup = useAddGroupToPost();
  const addCover = useAddCover();

  const result = useEntitySearch(query, config, options);
  const shouldRenderVerifyMenuItems = useShouldRenderVerifyMenuItems();

  const hasSeries = usePostSeries(post.id) != null;
  const shouldShowSeriesMenuItem =
    !hasSeries && !!post.publishedAt && !post.archivedAt;
  const shouldShowVerifyMenuItem =
    shouldRenderVerifyMenuItems({
      post,
      user: currentUser,
    }) && !post.verification;
  const shouldShowAddCoverMenuItem = !post.banner?.original && !isMobile;

  const sections = useMemo(() => {
    const grouped = _.groupBy(result, 'type');
    const groups = (grouped.group ?? []) as UseEntitySearchGroupResult[];
    const users = (grouped.user ?? []) as UseEntitySearchUserResult[];
    const topics = (grouped.topic ?? []) as UseEntitySearchTopicResult[];

    const addToPostItems = [
      shouldShowAddCoverMenuItem
        ? {
            id: 'cover',
            type: 'action' as const,
            label: 'Cover',
            icon: <BorderedIcon>{imageIcon}</BorderedIcon>,
            secondary: undefined,
            onSelect: addCover,
          }
        : null,
      shouldShowSeriesMenuItem
        ? {
            id: 'series',
            type: 'action' as const,
            label: 'Series',
            icon: <BorderedIcon>{seriesIcon}</BorderedIcon>,
            secondary: undefined,
            onSelect: () => {
              dispatch(UIActions.openModal(AddSeriesModal, { post }));
            },
          }
        : null,
      shouldShowVerifyMenuItem
        ? {
            id: 'verification',
            type: 'action' as const,
            label: 'Verification',
            icon: <BorderedIcon>{verificationIcon}</BorderedIcon>,
            secondary: undefined,
            onSelect: () => {
              dispatch(UIActions.openModal(CreateVerificationModal, { post }));
            },
          }
        : null,
    ]
      .filter((item): item is NonNullable<typeof item> => item != null)
      .filter((item) => item.label.toLowerCase().includes(query.toLowerCase()))
      .map((item) => {
        const start = item.label.toLowerCase().indexOf(query.toLowerCase());
        const end = start + query.length;
        const label =
          start === -1 ? (
            item.label
          ) : (
            <>
              {item.label.slice(0, start)}
              <strong>{item.label.slice(start, end)}</strong>
              {item.label.slice(end)}
            </>
          );

        return {
          ...item,
          label,
        };
      });

    const newTopicName = _.capitalize(query);

    return [
      {
        label: topics.length === 0 ? 'Add new topic' : 'Organize into topic',
        items: [
          ...topics.map((data) => ({
            id: data.id,
            type: 'topic' as const,
            data,
          })),
        ],
        createItem: {
          id: 'createTopic',
          type: 'action' as const,
          label: <strong>{newTopicName}</strong>,
          secondary: 'Create a new topic',
          onSelect: () => handleCreateTopic(newTopicName),
          icon: <BorderedIcon variant="topic">{addIcon}</BorderedIcon>,
        },
        preferredNumberOfItems: 2,
        isExpandable: true,
      },
      {
        label: 'Share directly',
        items: groups.map((data) => ({
          id: data.id,
          type: 'group' as const,
          data,
        })),
        preferredNumberOfItems: 1,
        isExpandable: false,
      },
      {
        label: groups.length === 0 ? 'Share directly' : null,
        items: users.map((data) => ({
          id: data.id,
          type: 'user' as const,
          data,
        })),
        preferredNumberOfItems: 1,
        isExpandable: false,
      },
      {
        label: 'Add to post',
        items: addToPostItems,
        preferredNumberOfItems: 3,
        isExpandable: false,
      },
    ];
  }, [
    result,
    query,
    handleCreateTopic,
    addCover,
    shouldShowSeriesMenuItem,
    shouldShowVerifyMenuItem,
    shouldShowAddCoverMenuItem,
    dispatch,
    post,
  ]);

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const inputValue = e.target.value;
    setInput(inputValue);

    startTransition(() => {
      setQuery(inputValue);
    });
  };

  const setIsShareDropdownOpen = useSetAtom(isShareDropdownOpenAtom);

  return (
    <ExpandableDropdown.Root
      onSelect={(item) => {
        switch (item.type) {
          case 'topic':
            addTopic(post.id, item.data.id);
            break;
          case 'user':
            addUser(post, item.data.id);
            dispatch(
              // eslint-disable-next-line react/no-unstable-nested-components
              UIActions.pushToast((toastProps) => (
                <Toast
                  level="success"
                  message={
                    <>
                      Post shared with user.{' '}
                      <Button
                        variant="link"
                        onClick={() => {
                          setIsShareDropdownOpen(true);
                          toastProps.onDismiss();
                        }}
                      >
                        View sharing
                      </Button>
                    </>
                  }
                  onDismiss={toastProps.onDismiss}
                />
              )),
            );
            break;
          case 'group':
            addGroup(post.id, item.data.id);
            dispatch(
              // eslint-disable-next-line react/no-unstable-nested-components
              UIActions.pushToast((toastProps) => (
                <Toast
                  level="success"
                  message={
                    <>
                      Post shared with group.{' '}
                      <Button
                        variant="link"
                        onClick={() => {
                          setIsShareDropdownOpen(true);
                          toastProps.onDismiss();
                        }}
                      >
                        View sharing
                      </Button>
                    </>
                  }
                  onDismiss={toastProps.onDismiss}
                />
              )),
            );
            break;
          case 'action':
            item.onSelect();
            break;
          default:
            exhaustiveMatchGuard(item);
        }

        onClose();
      }}
      maxItemsToShow={7}
      sections={sections}
      onEscape={onClose}
      renderItem={(item, { isFocused }) => {
        switch (item.type) {
          case 'topic':
            return (
              <TopicSelectOption
                matches={item.data.matches}
                topic={item.data.topic}
                focused={isFocused}
                truncate
              />
            );
          case 'user':
            return (
              <UserSelectOption
                matches={item.data.matches}
                user={item.data.user}
                name={item.data.name}
              />
            );
          case 'group':
            return (
              <GroupSelectOption
                matches={item.data.matches}
                group={item.data.group}
              />
            );
          case 'action':
            return (
              <SelectEntityOption
                label={item.label}
                icon={item.icon}
                secondary={item.secondary}
              />
            );
          default:
            exhaustiveMatchGuard(item);
        }

        return null;
      }}
    >
      <ExpandableDropdown.Input
        type="text"
        value={input}
        onChange={handleInputChange}
        className={styles.input}
        placeholder="Add to topic or share with..."
        autoFocus
      />
      <div className={styles.container}>
        <ExpandableDropdown.Menu />
      </div>
    </ExpandableDropdown.Root>
  );
};

export default ControlBarAddOrSharePopOver;
